import React, { useState, useEffect } from 'react';
import { FaHeart, FaPalette, FaSmile, FaGamepad, FaLeaf, FaExpand, FaTimes, FaChevronLeft, FaChevronRight, FaImages, FaPlay, FaVideo } from 'react-icons/fa';
import { 
  imageIndex, 
  imageMetadata, 
  validateImageName, 
  videoIndex, 
  videoMetadata, 
  validateVideoName,
  getVideosForCategory 
} from '../../data/imageIndex';


const Gallery = () => {
  const [selectedCategory, setSelectedCategory] = useState('todas');
  const [images, setImages] = useState({});
  const [videos, setVideos] = useState({});
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [imageError, setImageError] = useState({});
  const [videoError, setVideoError] = useState({});
  const [showVideos, setShowVideos] = useState(false); // Toggle entre fotos y videos

  // Agregar al inicio del componente Gallery
useEffect(() => {
  const shouldShowVideos = localStorage.getItem('showVideos');
  if (shouldShowVideos === 'true') {
    setShowVideos(true);
    localStorage.removeItem('showVideos'); // Limpiar después de usar
  }
}, []);
  const categories = [
    { 
      id: 'todas', 
      name: 'Todas', 
      icon: FaImages, 
      color: 'from-purple-400 to-pink-400',
      path: '' 
    },
    { 
      id: 'empatia', 
      name: 'Empatía', 
      icon: FaHeart, 
      color: 'from-pink-400 to-red-400',
      path: 'empatia' 
    },
    { 
      id: 'arte', 
      name: 'Arte', 
      icon: FaPalette, 
      color: 'from-blue-400 to-purple-400',
      path: 'arte' 
    },
    { 
      id: 'emociones', 
      name: 'Emociones', 
      icon: FaSmile, 
      color: 'from-yellow-400 to-orange-400',
      path: 'emociones' 
    },
    { 
      id: 'actividades', 
      name: 'Actividades', 
      icon: FaGamepad, 
      color: 'from-green-400 to-blue-400',
      path: 'actividades' 
    },
    { 
      id: 'naturaleza', 
      name: 'Naturaleza', 
      icon: FaLeaf, 
      color: 'from-green-400 to-teal-400',
      path: 'naturaleza' 
    }
  ];

  // Función para cargar imágenes desde el índice
  const loadImagesFromIndex = async () => {
    const imageData = {};
    
    // Procesar cada categoría
    for (const category of categories) {
      if (category.id !== 'todas') {
        const categoryImages = [];
        const imageList = imageIndex[category.id] || [];
        
        console.log(`Procesando categoría ${category.id} con ${imageList.length} imágenes`);
        
        // Validar nombres de archivo
        const invalidImages = imageList.filter(filename => !validateImageName(filename));
        if (invalidImages.length > 0) {
          console.warn(`Imágenes con nombres inválidos en ${category.id}:`, invalidImages);
        }
        
        // Crear objetos de imagen para cada archivo en el índice
        imageList.forEach((filename, index) => {
          const imagePath = `/images/gallery/${category.path}/${filename}`;
          const metadata = imageMetadata[category.id]?.[filename] || {};
          
          categoryImages.push({
            id: `${category.id}_${filename}`,
            src: imagePath,
            filename: filename,
            alt: metadata.description || `${category.name} - ${filename}`,
            title: metadata.title || `${category.name} - Imagen ${index + 1}`,
            description: metadata.description || '',
            category: category.id,
            index: index + 1,
            type: 'image'
          });
        });
        
        imageData[category.id] = categoryImages;
        console.log(`Cargadas ${categoryImages.length} imágenes para ${category.id}`);
      }
    }

    // Crear array "todas" combinando todas las categorías
    const allImages = Object.values(imageData).flat();
    imageData['todas'] = allImages;
    
    return imageData;
  };

  // Función para cargar videos desde el índice
  const loadVideosFromIndex = async () => {
    const videoData = {};
    
    // Procesar cada categoría
    for (const category of categories) {
      if (category.id !== 'todas') {
        const categoryVideos = [];
        const videoList = videoIndex[category.id] || [];
        
        console.log(`Procesando videos de categoría ${category.id} con ${videoList.length} videos`);
        
        // Validar nombres de archivo
        const invalidVideos = videoList.filter(filename => !validateVideoName(filename));
        if (invalidVideos.length > 0) {
          console.warn(`Videos con nombres inválidos en ${category.id}:`, invalidVideos);
        }
        
        // Crear objetos de video para cada archivo en el índice
        videoList.forEach((filename, index) => {
          const videoPath = `/videos/gallery/${category.path}/${filename}`;
          const metadata = videoMetadata[category.id]?.[filename] || {};
          
          categoryVideos.push({
            id: `${category.id}_${filename}`,
            src: videoPath,
            filename: filename,
            alt: metadata.description || `${category.name} - ${filename}`,
            title: metadata.title || `${category.name} - Video ${index + 1}`,
            description: metadata.description || '',
            category: category.id,
            index: index + 1,
            type: 'video'
          });
        });
        
        videoData[category.id] = categoryVideos;
        console.log(`Cargados ${categoryVideos.length} videos para ${category.id}`);
      }
    }

    // Crear array "todas" combinando todas las categorías
    const allVideos = Object.values(videoData).flat();
    videoData['todas'] = allVideos;
    
    return videoData;
  };

  // Cargar imágenes y videos al montar el componente
  useEffect(() => {
    setLoading(true);
    
    const loadContent = async () => {
      try {
        const [imageData, videoData] = await Promise.all([
          loadImagesFromIndex(),
          loadVideosFromIndex()
        ]);
        
        setImages(imageData);
        setVideos(videoData);
        
        console.log('Contenido cargado:', {
          imágenes: Object.keys(imageData).reduce((acc, key) => {
            acc[key] = imageData[key].length;
            return acc;
          }, {}),
          videos: Object.keys(videoData).reduce((acc, key) => {
            acc[key] = videoData[key].length;
            return acc;
          }, {})
        });
      } catch (error) {
        console.error('Error cargando contenido:', error);
        // En caso de error, crear estructura vacía
        const emptyData = {};
        categories.forEach(category => {
          if (category.id !== 'todas') {
            emptyData[category.id] = [];
          }
        });
        emptyData['todas'] = [];
        setImages(emptyData);
        setVideos(emptyData);
      }
      
      setLoading(false);
    };

    loadContent();
  }, []);

  // Obtener contenido filtrado (imágenes o videos)
  const getFilteredContent = () => {
    const contentData = showVideos ? videos : images;
    const categoryContent = contentData[selectedCategory] || [];
    const errorData = showVideos ? videoError : imageError;
    
    // Filtrar contenido que ha fallado al cargar
    return categoryContent.filter(item => !errorData[item.id]);
  };

  // Abrir modal de imagen
  const openImageModal = (image, index) => {
    if (image.type === 'video') {
      setSelectedVideo(image);
      setSelectedImage(null);
    } else {
      setSelectedImage(image);
      setSelectedVideo(null);
    }
    setCurrentIndex(index);
  };

  // Cerrar modal
  const closeModal = () => {
    setSelectedImage(null);
    setSelectedVideo(null);
    setCurrentIndex(0);
  };

  // Navegación en modal
  const navigateContent = (direction) => {
    const filteredContent = getFilteredContent();
    let newIndex;
    
    if (direction === 'next') {
      newIndex = currentIndex + 1 >= filteredContent.length ? 0 : currentIndex + 1;
    } else {
      newIndex = currentIndex - 1 < 0 ? filteredContent.length - 1 : currentIndex - 1;
    }
    
    setCurrentIndex(newIndex);
    const newContent = filteredContent[newIndex];
    
    if (newContent.type === 'video') {
      setSelectedVideo(newContent);
      setSelectedImage(null);
    } else {
      setSelectedImage(newContent);
      setSelectedVideo(null);
    }
  };

  // Manejar errores de carga
  const handleContentError = (contentId, contentSrc, filename, type) => {
    console.error(`Error cargando ${type}: ${contentSrc} (${filename})`);
    if (type === 'video') {
      setVideoError(prev => ({ ...prev, [contentId]: true }));
    } else {
      setImageError(prev => ({ ...prev, [contentId]: true }));
    }
  };

  // Manejar carga exitosa
  const handleContentLoad = (contentId, contentSrc, filename, type) => {
    console.log(`✓ ${type} cargado: ${filename}`);
  };

  const filteredContent = getFilteredContent();

  // Contar contenido por categoría
  const getContentCount = (categoryId) => {
    const contentData = showVideos ? videos : images;
    const categoryContent = contentData[categoryId] || [];
    const errorData = showVideos ? videoError : imageError;
    return categoryContent.filter(item => !errorData[item.id]).length;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-green-50 to-yellow-50 py-16">
      <div className="container mx-auto px-4">
        {/* Header de la galería */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center space-x-2 bg-white/80 rounded-full px-6 py-3 backdrop-blur-sm mb-6">
            <FaImages className="text-azul-cielo text-xl" />
            <span className="font-medium text-gray-700">Galería FeelLab</span>
          </div>
          
          <h1 className="text-4xl md:text-5xl font-titulo font-bold text-gray-800 mb-4">
            Nuestros
            <span className="bg-gradient-to-r from-azul-cielo to-verde-suave bg-clip-text text-transparent">
              {' '}Momentos Mágicos
            </span>
          </h1>
          
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Descubre las experiencias únicas que vivimos en el Laboratorio de Empatía Animal,
            donde cada momento es una oportunidad para conectar con las emociones y la naturaleza.
          </p>
        </div>

        {/* Toggle entre fotos y videos */}
        <div className="flex justify-center mb-8">
          <div className="bg-white/80 backdrop-blur-sm rounded-full p-2 shadow-lg border border-gray-200">
            <button
              onClick={() => setShowVideos(false)}
              className={`px-6 py-2 rounded-full font-medium transition-all duration-300 flex items-center space-x-2 ${
                !showVideos 
                  ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white shadow-md' 
                  : 'text-gray-600 hover:text-blue-500 hover:bg-blue-50'
              }`}
            >
              <FaImages />
              <span>Fotos</span>
            </button>
            <button
              onClick={() => setShowVideos(true)}
              className={`px-6 py-2 rounded-full font-medium transition-all duration-300 flex items-center space-x-2 ${
                showVideos 
                  ? 'bg-gradient-to-r from-red-500 to-pink-500 text-white shadow-md' 
                  : 'text-gray-600 hover:text-red-500 hover:bg-red-50'
              }`}
            >
              <FaVideo />
              <span>Videos</span>
            </button>
          </div>
        </div>

        {/* Filtros de categorías */}
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          {categories.map((category) => {
            const IconComponent = category.icon;
            const isActive = selectedCategory === category.id;
            const contentCount = getContentCount(category.id);
            
            return (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`group flex items-center space-x-3 px-6 py-3 rounded-full font-medium transition-all duration-300 transform hover:scale-105 ${
                  isActive
                    ? `bg-gradient-to-r ${category.color} text-white shadow-lg`
                    : 'bg-white text-gray-700 hover:bg-gray-50 shadow-md'
                }`}
              >
                <IconComponent className={`text-lg ${isActive ? 'text-white' : 'text-gray-500'}`} />
                <span>{category.name}</span>
                <span className={`text-sm px-2 py-1 rounded-full ${
                  isActive ? 'bg-white/30' : 'bg-gray-200'
                }`}>
                  {contentCount}
                </span>
              </button>
            );
          })}
        </div>

        {/* Grid de contenido */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {[...Array(8)].map((_, index) => (
              <div key={index} className="aspect-square bg-gray-200 rounded-2xl animate-pulse"></div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {filteredContent.map((content, index) => (
              <div
                key={content.id}
                className="group relative aspect-square bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 cursor-pointer"
                onClick={() => openImageModal(content, index)}
              >
                {content.type === 'video' ? (
                  <div className="relative w-full h-full">
                    <video
                      src={content.src}
                      className="w-full h-full object-cover"
                      onError={() => handleContentError(content.id, content.src, content.filename, 'video')}
                      onLoadedData={() => handleContentLoad(content.id, content.src, content.filename, 'video')}
                      muted
                      playsInline
                    />
                    <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                      <FaPlay className="text-white text-3xl drop-shadow-lg" />
                    </div>
                  </div>
                ) : (
                  <img
                    src={content.src}
                    alt={content.alt}
                    className="w-full h-full object-cover"
                    onError={() => handleContentError(content.id, content.src, content.filename, 'image')}
                    onLoad={() => handleContentLoad(content.id, content.src, content.filename, 'image')}
                    loading="lazy"
                  />
                )}
                
                {/* Overlay con efecto hover */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="absolute bottom-4 left-4 right-4">
                    <h3 className="text-white font-medium text-sm mb-1">{content.title}</h3>
                    <div className="flex items-center justify-between">
                      <span className="text-white/80 text-xs capitalize">{content.category}</span>
                      <FaExpand className="text-white text-sm" />
                    </div>
                    {content.description && (
                      <p className="text-white/70 text-xs mt-1 line-clamp-2">{content.description}</p>
                    )}
                  </div>
                </div>

                {/* Indicador de categoría */}
                <div className="absolute top-3 right-3">
                  <div className={`w-3 h-3 rounded-full bg-gradient-to-r ${
                    categories.find(c => c.id === content.category)?.color || 'from-gray-400 to-gray-500'
                  }`}></div>
                </div>

                {/* Indicador de tipo de contenido */}
                <div className="absolute top-3 left-3 bg-black/50 text-white text-xs px-2 py-1 rounded-full flex items-center space-x-1">
                  {content.type === 'video' ? <FaVideo /> : <FaImages />}
                  <span>{content.index}</span>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Mensaje cuando no hay contenido */}
        {!loading && filteredContent.length === 0 && (
          <div className="text-center py-16">
            {showVideos ? <FaVideo className="text-gray-400 text-6xl mx-auto mb-4" /> : <FaImages className="text-gray-400 text-6xl mx-auto mb-4" />}
            <h3 className="text-xl font-medium text-gray-600 mb-2">
              No hay {showVideos ? 'videos' : 'imágenes'} disponibles en esta categoría
            </h3>
            <p className="text-gray-500">
              Pronto agregaremos más contenido a nuestra galería
            </p>
          </div>
        )}
      </div>

      {/* Modal de imagen/video ampliado */}
      {(selectedImage || selectedVideo) && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4">
          <div className="relative max-w-4xl max-h-full">
            {/* Botón cerrar */}
            <button
              onClick={closeModal}
              className="absolute top-4 right-4 z-10 bg-white/20 backdrop-blur-sm text-white p-2 rounded-full hover:bg-white/30 transition-colors"
            >
              <FaTimes className="text-xl" />
            </button>

            {/* Navegación */}
            {filteredContent.length > 1 && (
              <>
                <button
                  onClick={() => navigateContent('prev')}
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 z-10 bg-white/20 backdrop-blur-sm text-white p-3 rounded-full hover:bg-white/30 transition-colors"
                >
                  <FaChevronLeft className="text-xl" />
                </button>

                <button
                  onClick={() => navigateContent('next')}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 z-10 bg-white/20 backdrop-blur-sm text-white p-3 rounded-full hover:bg-white/30 transition-colors"
                >
                  <FaChevronRight className="text-xl" />
                </button>
              </>
            )}

            {/* Contenido principal */}
            {selectedVideo ? (
              <video
                src={selectedVideo.src}
                className="max-w-full max-h-[80vh] object-contain rounded-lg"
                controls
                autoPlay
              />
            ) : (
              <img
                src={selectedImage.src}
                alt={selectedImage.alt}
                className="max-w-full max-h-[80vh] object-contain rounded-lg"
              />
            )}

            {/* Información del contenido */}
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6 rounded-b-lg">
              <h3 className="text-white text-xl font-medium mb-2">
                {selectedVideo ? selectedVideo.title : selectedImage?.title}
              </h3>
              {(selectedVideo?.description || selectedImage?.description) && (
                <p className="text-white/80 text-sm mb-2">
                  {selectedVideo ? selectedVideo.description : selectedImage?.description}
                </p>
              )}
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <span className="text-white/80 capitalize">
                    {selectedVideo ? selectedVideo.category : selectedImage?.category}
                  </span>
                  <span className="text-white/60 text-sm">
                    {selectedVideo ? selectedVideo.filename : selectedImage?.filename}
                  </span>
                  <span className="text-white/60 text-sm flex items-center space-x-1">
                    {selectedVideo ? <FaVideo /> : <FaImages />}
                    <span>{selectedVideo ? 'Video' : 'Imagen'}</span>
                  </span>
                </div>
                <span className="text-white/60 text-sm">
                  {currentIndex + 1} de {filteredContent.length}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Gallery;